import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

export type VoiceChatMessage = {
  id: string;
  role: "user" | "agent";
  text: string;
};

interface VoiceTranscriptBubbleProps {
  open: boolean;
  isSpeaking: boolean;
  messages: VoiceChatMessage[];
  onClose: () => void;
  /** Tailwind positioning utilities. Defaults to inline (no positioning). */
  positionClassName?: string;
  /** Bubble width. Default fits floating widget. */
  widthClassName?: string;
}

const VoiceTranscriptBubble: React.FC<VoiceTranscriptBubbleProps> = ({
  open,
  isSpeaking,
  messages,
  onClose,
  positionClassName = "",
  widthClassName = "w-[min(85vw,320px)]",
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.96 }}
          transition={{ duration: 0.18 }}
          className={`${positionClassName} ${widthClassName} rounded-2xl border border-border/60 bg-card/95 backdrop-blur-sm shadow-xl overflow-hidden`}
          role="log"
          aria-live="polite"
          aria-label={t("cta_voice_transcript", "Transcription en direct")}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-border/40 bg-foreground/5">
            <div className="flex items-center gap-2">
              <span
                className={`h-2 w-2 rounded-full ${
                  isSpeaking ? "bg-blue-500 animate-pulse" : "bg-green-500"
                }`}
              />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground/70">
                {isSpeaking
                  ? t("cta_voice_speaking", "Parle…")
                  : t("cta_voice_listening", "Écoute…")}
              </span>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label={t("close", "Fermer")}
              className="rounded-full p-1 text-foreground/50 hover:bg-foreground/10 hover:text-foreground/80 transition"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div
            ref={scrollRef}
            className="max-h-64 overflow-y-auto px-3 py-2.5 space-y-2"
          >
            {messages.length === 0 ? (
              <p className="text-xs text-foreground/50 italic py-2 text-center">
                {t("cta_voice_waiting", "En attente de la conversation…")}
              </p>
            ) : (
              messages.map((m) => (
                <div
                  key={m.id}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-xs leading-snug ${
                      m.role === "user"
                        ? "bg-accent text-accent-foreground rounded-br-sm"
                        : "bg-foreground/8 text-foreground rounded-bl-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VoiceTranscriptBubble;
